import type { OpenStage, ScheduleTarget, TaskPriority, TaskSummary } from '@/lib/api/types';
import { OpenSubtaskPreview } from '@/components/tasks/Subtasks';
import { TaskRow } from '@/components/tasks/TaskRow';

export interface TaskActions {
  onOpen: (taskId: string) => void;
  onToggleComplete: (task: TaskSummary) => void;
  /** The second stage: "yes, really done" on a row ticked but not confirmed. */
  onConfirmComplete: (task: TaskSummary) => void;
  /**
   * Complete a checklist step directly, skipping the pending stage.
   *
   * A step ticked in the preview has no row of its own to carry the
   * confirmation buttons, and its parent stays right there with the counter —
   * so the two-stage ceremony would protect nothing and confuse the tick.
   */
  onCompleteStep: (task: TaskSummary) => void;
  onMove: (taskId: string, target: ScheduleTarget, date?: string) => void;
  onSetPriority: (taskId: string, priority: TaskPriority) => void;
  /** Move between the open stages. Finishing stays the tick box. */
  onSetStage: (taskId: string, status: OpenStage) => void;
  onDelete: (taskId: string) => void;
}

export function TaskList({
  tasks,
  today,
  currentUserId,
  actions,
  showDate = false,
  overdue = false,
  showOpenSubtasks = false,
}: {
  tasks: TaskSummary[];
  today: string;
  currentUserId: string;
  actions: TaskActions;
  showDate?: boolean;
  overdue?: boolean;
  /** List each task's unfinished steps beneath it — used on Today. */
  showOpenSubtasks?: boolean;
}) {
  return (
    <ul className="space-y-2.5 max-sm:space-y-3 touch:space-y-3">
      {tasks.map((task) => (
        <li key={task.id}>
          <TaskRow
            task={task}
            today={today}
            currentUserId={currentUserId}
            showDate={showDate}
            overdue={overdue}
            {...actions}
          />
          {showOpenSubtasks && task.subtask_total > task.subtask_done ? (
            <OpenSubtaskPreview task={task} onToggle={actions.onCompleteStep} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * A titled group of tasks — a priority band on Today, a day in Upcoming.
 *
 * The heading carries a count because the number is the useful part when
 * scanning: "MUSÍM 2" answers a question that "MUSÍM" does not.
 */
export function TaskGroup({
  title,
  count,
  children,
  tone = 'default',
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
  tone?: 'default' | 'overdue';
}) {
  return (
    <section className="mb-6 last:mb-0">
      <h2
        className={
          tone === 'overdue'
            ? 'mb-1.5 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-wider text-overdue'
            : 'mb-1.5 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-wider text-content-subtle'
        }
      >
        {title}
        {count !== undefined && count > 0 ? (
          <span className="font-normal tabular-nums opacity-70">{count}</span>
        ) : null}
      </h2>
      {children}
    </section>
  );
}
